import { HelpButton } from '@components/help/HelpButton'
import Page from '@components/Page/Page'
import { BooleanTag } from '@components/utils/BooleanTag'
import SBNiceDate from '@components/utils/SBNiceDate'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { ExternalBookingAPIKey } from '@models/ExternalBookingAPIKey'
import {
  getExternalBookingAPIKeys,
  selectExternalBookingAPIKeys,
  setExternalBookingAPIKeyQuery,
  setTempCreatedApiKey,
} from '@store/ExternalBookingAPIKeyReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import { Alert, Col, Space, Typography, type TableColumnsType } from 'antd'
import { useEffect } from 'react'

const { Text } = Typography

const ExternalBookingAPIPage = () => {
  const { isOpen } = useSBRM()
  const dispatch = useAppDispatch()
  const externalBookingAPIKeys = useAppSelector(selectExternalBookingAPIKeys())
  const { query, isLoading, tempCreatedApiKey } = useAppSelector(
    (state) => state.externalBookingAPIKey
  )

  useEffect(() => {
    return () => {
      dispatch(setTempCreatedApiKey(undefined))
    }
  }, [dispatch])

  const columns: TableColumnsType<ExternalBookingAPIKey> = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Is active',
      render: (record: ExternalBookingAPIKey) => (
        <BooleanTag value={record.isActive} />
      ),
    },
    {
      title: 'Last used at',
      render: (record: ExternalBookingAPIKey) => (
        <SBNiceDate date={record.lastUsedAt} />
      ),
    },
    {
      key: 'createdAt',
      title: 'Created at',
      render: (record: ExternalBookingAPIKey) => (
        <SBNiceDate date={record.createdAt} />
      ),
    },
    {
      key: 'updatedAt',
      title: 'Updated at',
      render: (record: ExternalBookingAPIKey) => (
        <SBNiceDate date={record.updatedAt} />
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      align: 'right',
      render: (record: ExternalBookingAPIKey) => (
        <SBRM.Actions
          entityId={record.id}
          entity={Entity.externalBookingAPIKey}
          actions={[SBRMAction.Update]}
        />
      ),
    },
  ]

  const fetchExternalBookingAPIKeys = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
    }

    dispatch(setExternalBookingAPIKeyQuery(newQuery))
    dispatch(getExternalBookingAPIKeys(newQuery))
  }

  const { handleTableChange, params } = useTableUrlSync<ExternalBookingAPIKey>({
    onParamsChange: fetchExternalBookingAPIKeys,
  })

  useEffect(() => {
    if (isOpen) return

    fetchExternalBookingAPIKeys(params)
  }, [isOpen])

  return (
    <Page
      noWrapWithCol
      permissions={Permission.manageApiKeys}
      pageTitle={{
        title: 'External Booking API keys',
        level: 4,
        toolbar: (
          <Space>
            <HelpButton
              block
              label="Open the API documentation"
              externalLink="https://plethore-booking-api-documentation.vercel.app"
            />
            <SBRM.Actions
              entity={Entity.externalBookingAPIKey}
              actions={[SBRMAction.Create]}
            />
          </Space>
        ),
      }}
    >
      <Col md={24}>
        <Alert
          type="warning"
          title="We provide an external Booking API designed to allow seamless
              integration of our reservation capabilities into third-party
              systems. The API is currently in beta, meaning that while core
              features are available and stable, the interface and behavior may
              evolve. Breaking changes may occur, and performance or edge cases
              are still being actively monitored. We encourage integrators to
              use this version for testing and development purposes and to share
              feedback to help us improve reliability and completeness ahead of
              the general release."
        />
      </Col>
      {tempCreatedApiKey && (
        <Col md={24}>
          <Alert
            type="error"
            title="Make sure to copy your API key. You won't be able to see it again once you leave this page."
            description={<Text copyable>{tempCreatedApiKey}</Text>}
            closable
            styles={{
              title: {
                color: 'red',
              },
            }}
          />
        </Col>
      )}
      <Col md={24}>
        <SBTable
          columns={columns}
          dataSource={externalBookingAPIKeys}
          loading={isLoading}
          onChange={handleTableChange}
          pagination={query.pagination}
          scroll={{ x: 'max-content' }}
        />
      </Col>
    </Page>
  )
}

export default ExternalBookingAPIPage
